/**
 * Configuration → Business Opportunities. Everything that shapes business-opportunity research,
 * on its one process, in four parts: the AI model it runs on, the dimensions ideas are scored on,
 * the Impact × Feasibility matrix (axes and split; presentation only), and the versioned prompt.
 * The open part is in `&part=`, so it links; an idea's page links to a prompt version with
 * `&part=prompt&version=<n>`, and a `version` opens the Prompt part whatever `&part=` says.
 * Each part shows to whoever may view it; its edit controls need `.manage`.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ProcessAssignments } from '@/routes/ai-models/ProcessAssignments';

import { DimensionsSection } from './DimensionsSection';
import { MatrixSection } from './MatrixSection';
import { ProcessWorkbench, type ProcessInfo, type ProcessPart } from './ProcessWorkbench';
import { PromptVersionsSection } from './PromptVersionsSection';

/** The AI process business-opportunity research runs (backend app/ai/processes.py). */
export const BUSINESS_OPPORTUNITY_PROCESS = 'discovery.business-opportunity-research';

/** What the list shows a caller who may not read the assignments. */
const KNOWN: ProcessInfo[] = [
  {
    key: BUSINESS_OPPORTUNITY_PROCESS,
    name: 'Business opportunity research',
    description:
      'Researches a submitted idea as a business opportunity: market, competition, GFT fit, scores and a full report.',
    has_prompt: true,
  },
];

const PARTS: ProcessPart[] = [
  { key: 'model', label: 'AI model', permission: 'ai.processes.view', render: () => <ProcessAssignments processKeys={[BUSINESS_OPPORTUNITY_PROCESS]} /> },
  { key: 'dimensions', label: 'Dimensions', permission: 'research.dimensions.view', render: () => <DimensionsSection /> },
  { key: 'matrix', label: 'Matrix', permission: 'research.matrix.view', render: () => <MatrixSection /> },
  { key: 'prompt', label: 'Prompt', permission: 'research.prompt.view', render: () => <PromptVersionsSection /> },
];

export function BusinessOpportunitiesTab() {
  const [params, setParams] = useSearchParams();
  const version = params.get('version');
  const part = params.get('part');
  useEffect(() => {
    if (!version || part === 'prompt') return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('part', 'prompt');
        return next;
      },
      { replace: true },
    );
  }, [version, part, setParams]);

  return <ProcessWorkbench label="Business Opportunities processes" keys={[BUSINESS_OPPORTUNITY_PROCESS]} parts={() => PARTS} known={KNOWN} />;
}
